const BufferedDebouncedTcpSocketConnection = require('@/lib/Net/BufferedDebouncedTcpSocketConnection');
const DebouncedTcpSocketConnection = require('@/lib/Net/DebouncedTcpSocketConnection');
const logFactory = require('@/libraries/DigiTickets/Logging/logFactory');
const PrintJobOptions = require('@/lib/Printing/PrintJobOptions');
const PrintType = require('@/lib/Printing/PrintType');
const VerifoneActionNames = require('@/lib/PaymentMethods/Verifone/Constants/VerifoneActionNames');
const VerifoneContinueTransactionRecord = require('./Records/Request/VerifoneContinueTransactionRecord');
const VerifoneCustomReprintMerchantReceiptRecord = require('./Records/Request/VerifoneCustomReprintMerchantReceiptRecord');
const VerifoneIntegrationType = require('./VerifoneIntegrationType');
const VerifoneLoginRequestRecord = require('./Records/Request/VerifoneLoginRequestRecord');
const VerifoneLoginResponseRecord = require('./Records/Response/VerifoneLoginResponseRecord');
const VerifoneLogoutRequestRecord = require('./Records/Request/VerifoneLogoutRequestRecord');
const VerifoneLogoutResponseRecord = require('./Records/Response/VerifoneLogoutResponseRecord');
const VerifoneReportRequestRecord = require('./Records/Request/VerifoneReportRequestRecord');
const VerifoneSocketNames = require('./VerifoneSocketNames');
const VerifoneStatusNames = require('./Constants/VerifoneStatusNames');
const VerifoneStatusRecord = require('./Records/Response/VerifoneStatusRecord');
const VerifoneSubmitOfflineTransactionsRequestRecord = require('./Records/Request/VerifoneSubmitOfflineTransactionsRequestRecord');
const VerifoneSubmitOfflineTransactionsResponseRecord = require('./Records/Response/VerifoneSubmitOfflineTransactionsResponseRecord');
const VerifoneVoucherRecord = require('./Records/Response/VerifoneVoucherRecord');
const VerifoneWinStateRequestRecord = require('./Records/Request/VerifoneWinStateRequestRecord');
const VerifoneXmlReceiptRenderer = require('@digitickets/verifone-xml-receipt-renderer');
const { timeoutPromise } = require('@/functions/functions');

/**
 * @constructor
 *
 * @param $interval
 * @param $timeout
 * @param {AgentService} agentService
 * @param {ConnectivityChecker} connectivityChecker
 * @param {DeviceManager} deviceManager
 * @param {DialogService} dialogService
 * @param {ReceiptPrinter} receiptPrinter
 * @param {ToastFactory} toastFactory
 * @param {UserService} userService
 * @param {VerifoneConfig} verifoneConfig
 * @param {VerifoneResponseRecordFactory} verifoneResponseRecordFactory
 */
const VerifoneDriver = function (
    $interval,
    $timeout,
    agentService,
    connectivityChecker,
    deviceManager,
    dialogService,
    receiptPrinter,
    toastFactory,
    userService,
    verifoneConfig,
    verifoneResponseRecordFactory
) {
    this.$timeout = $timeout;
    this.agentService = agentService;
    this.config = verifoneConfig;
    this.connectivityChecker = connectivityChecker;
    this.deviceManager = deviceManager;
    this.dialogService = dialogService;
    this.receiptPrinter = receiptPrinter;
    this.toastFactory = toastFactory;
    this.userService = userService;
    this.verifoneResponseRecordFactory = verifoneResponseRecordFactory;

    /**
     * @type {VerifoneXmlReceiptRenderer}
     */
    this.xmlReceiptRenderer = new VerifoneXmlReceiptRenderer();
    this.xmlReceiptRenderer.setCustomHeader(this.config.customReceiptHeader);
    this.xmlReceiptRenderer.setCustomFooter(this.config.customReceiptFooter);

    /**
     * @type {ConsoleLogger}
     */
    this.log = logFactory('Verifone', true);

    /**
     * Callbacks to be fired when a message is received from the terminal.
     *
     * @type {Function[]}
     */
    this.recordReceivedListeners = [];

    /**
     * The socket connection to the terminal for
     * - sending commands (VX820 + VX820IP)
     * - receiving results (VX820 + VX820IP)
     * - receiving progress messages (VX820IP only)
     *
     * The integration socket regularly disconnects itself (by design), so we must ensure it is connected
     * before sending any message to it.
     *
     * @type {TcpSocketConnection}
     */
    this.integrationSocket = null;

    /**
     * When using the POS Client (VX820) a separate socket connection is needed to receive status updates
     * ("Card inserted", "Waiting for PIN", etc.) Note that the POS Client must be in
     * 'screenless' mode for the progress socket to be enabled.
     *
     * When not using the POS Client (VX820IP) these messages are received on the integration socket and
     * the progress socket is not required or available.
     *
     * @type {?TcpSocketConnection}
     */
    this.progressSocket = null;

    /**
     * Keep track of the state of the terminal.
     */
    this.state = {
        /**
         * Can a cancel command be sent in the current state?
         *
         * @type {boolean|null}
         */
        canCancel: null,

        /**
         * To prevent sending records to the terminal too quickly a 1 second timeout happens after
         * each record is sent. During this time buttons should be disabled so the operator cannot
         * press it again.
         *
         * Note that this does not actually prevent records being sent by the driver and
         * is only used to update the UI.
         *
         * @type {boolean}
         */
        canSendRecord: true,

        /**
         * Has the device been 'ready' at least once since the EPOS was loaded?
         *
         * @type {boolean}
         */
        initialReady: false,

        /**
         * Is the device in the 'ready' state currently?
         * (null = we don't know)
         *
         * @type {boolean|null}
         */
        isReady: null,

        /**
         * Last record received from the integration socket.
         */
        lastIntegrationRecordReceived: null,

        /**
         * Last record received from the progress socket.
         *
         * @type {VerifoneStatusRecord|null}
         */
        lastProgressRecordReceived: null,

        /**
         * Remember the last record we sent to the terminal.
         * (Always on the integration socket because you don't send things to the progress socket.)
         *
         * @type {*}
         */
        lastRecordSent: null,

        /**
         * @type {VerifoneStatusRecord|null}
         */
        lastMerchantReceiptRecord: null,

        /**
         * @type {VerifoneStatusRecord|null}
         */
        lastCustomerReceiptRecord: null,

        /**
         * Is a user logged in to the payment terminal?
         * (null = we don't know)
         *
         * @type {Boolean|null}
         */
        loggedIn: null,

        /**
         * Is the "go offline" / "attempt to reconnect" modal currently displayed?
         */
        offlineConfirmationPending: false,
    };

    /**
     * The submitOfflineTransactions method returns a Promise which needs to resolve
     * once we have heard the result back from the device.
     * To do that, when a "submit offline txns" request is set to an object containing the
     * resolve function to be called when we get the response from the terminal.
     *
     * @type {{resolve: function, reject: function}|null}
     */
    this.submitOfflineTransactionRequestCallbacks = null;

    /**
     * The same as submitOfflineTransactionRequestCallbacks but for a logout request.
     * TODO: It would be a good idea to make these generic otherwise we might end up with a 'xCallbacks' property
     * for each request type that can be sent, although there aren't that many different types.
     *
     * @type {{resolve: function, reject: function}|null}
     */
    this.logoutRequestCallbacks = null;

    let configValid = false;
    try {
        configValid = this.config.validate();
    } catch (e) {
        toastFactory.errorTop(
            'VERIFONE_SETTINGS.CONFIG_ERROR',
            e.message
        );
    }

    if (configValid) {
        this.init();
    }

    /**
     * While the terminal is logging in or downloading updates a persistent notification will be displayed.
     * Keep a reference to the method to dismiss those notifications here so they can be dismissed later.
     */
    this.dismissLoggingInNotificationFunc = null;
    this.dismissUpdatingNotificationFunc = null;

    // When we go back online, tell the terminal to submit its offline transactions.
    this.connectivityChecker.addObserver({
        key: 'VerifoneDriver',
        notifyOnline: () => {
            if (this.state.isReady) {
                // Temporarily disabled until we figure out why this breaks (PRO-780)
                // this.submitOfflineTransactions();
            }
        },
        notifyOffline: () => {
        },
    });

    // Submit offline transactions every 5 mins.
    // This is commented out as it can cause errors to appear if it runs while a transaction is in progress.
    // TODO: Determine if this this needed or if the online observer is sufficient.
    // $interval(() => {
    //    if (this.state.isReady && this.connectivityChecker.online) {
    //        this.submitOfflineTransactions()
    //    }
    // }, 300000);

    // Enable extended receipts if the config says so.
    this.setExtendedReceipts(this.config.extendedReceipts);
};

VerifoneDriver.prototype = {
    /**
     * Setup the socket connections.
     */
    init() {
        // The integration socket will disconnect after a period of inactivity or after an error.
        this.integrationSocket = this.createTcpSocket(
            VerifoneSocketNames.INTEGRATION,
            this.config.terminalHost,
            this.config.terminalPort
        );
        let integrationSocketPromise = this.integrationSocket.connect();
        integrationSocketPromise.catch((error) => {
            this.toastFactory.error(
                'PAYMENT.TERMINAL_ERROR',
                'There was a problem connecting to the Verifone integration socket.<br/>'
                + error.toString()
            );
        });

        Promise
            .all([
                integrationSocketPromise,
                this.reconnectProgressSocket(),
            ])
            .then(() => {
                this.toastFactory.success(
                    '',
                    'Connected to Verifone payment terminal.'
                );
            })
            .catch(() => {
                this.dismissLoggingInNotification();
            });
    },

    reconnectProgressSocket() {
        if (!this.config.posClient) {
            // Progress socket is not used.
            return Promise.resolve();
        }

        // Disconnect any existing connection first
        let disconnectPromise;

        if (this.progressSocket) {
            this.log.debug('Disconnecting existing progress socket.');
            disconnectPromise = this.progressSocket.disconnect()
                .catch(() => {})
                .then(() => {
                    this.log.debug('Progress socket disconnected.');
                });
        } else {
            disconnectPromise = Promise.resolve();
        }

        return disconnectPromise
            .then(() => {
                this.log.debug('Connecting progress socket.');

                // The progress socket stays connected indefinitely.
                this.progressSocket = this.createTcpSocket(
                    VerifoneSocketNames.PROGRESS,
                    this.config.terminalHost,
                    this.config.terminalProgressPort
                );

                return this.progressSocket.connect();
            })
            .then(() => {
                // Very shortly after you connect to the progress socket it will send you a message containing the
                // present status of the terminal.
                // This can lead to a problem when reconnecting the progress socket right before starting a transaction.
                // We reconnect the progress socket, then send the integration command to start the transaction
                // immediately, and then a few ms later we get that initial status on the progress socket.
                // That will probably be "Ready", because that's what it was when we connected, but it's no longer true
                // because we started a transaction.
                // For that reason we add a little delay here to leave time for the terminal to respond with the
                // current status before we do anything else.
                return timeoutPromise(500);
            })
            .catch((error) => {
                this.toastFactory.error(
                    'PAYMENT.TERMINAL_ERROR',
                    'There was a problem connecting to the Verifone progress socket.<br/>'
                    + error.toString()
                );

                // Re-throw the error so the promise stays rejected.
                throw error;
            });
    },

    /**
     * Create a new TCP socket object via the WebSocket connection.
     * Setup listeners to handle data and errors.
     *
     * @private
     * @param {string} name
     * @param {string} host
     * @param {number} port
     *
     * @return {TcpSocketConnection}
     */
    createTcpSocket(name, host, port) {
        // Sometimes messages from terminals exceed the size of a single TCP message so are sent in multiple.
        // To cater for that we buffer all data until a CRLF (\r\n) is found, which marks the end of a message.
        // BufferedTcpSocketConnection handles this.
        // However some integrations never end that CRLF so we can't use the buffered connection.
        // We'll use DebouncedTcpSocketConnection by default which debounces sending messages to comply with Verifone's
        // advice to not send messages too fast, and use the Buffered and Debounced connection only where we know
        // it is safe to do so.
        let connectionClass = DebouncedTcpSocketConnection;

        if (this.config.integrationType === VerifoneIntegrationType.ENGAGE) {
            // Engage terminals correctly end messages on both the integration and progress ports with CRLF.
            // We'll also use the version which debounces sending messages to comply with Verifone's advice to not
            // send messages too fast.
            connectionClass = BufferedDebouncedTcpSocketConnection;
        } else if (
            this.config.integrationType === VerifoneIntegrationType.POS_CLIENT
            && name === VerifoneSocketNames.PROGRESS
        ) {
            // Despite what the docs claim, POS Client does NOT terminate integration messages with CRLF, even
            // when setting it to do so. So we'll only use the buffered connection for the POS Client progress socket
            // which *does* correctly terminate messages with CRLF, and the POS Client integration socket will default
            // to using the debounced but not buffered socket (defined above).
            connectionClass = BufferedDebouncedTcpSocketConnection;
        }

        let tcpSocket = new connectionClass(
            name,
            host,
            port,
            this.agentService
        );
        this.agentService.addTcpSocket(tcpSocket);

        if (this.config.integrationType === VerifoneIntegrationType.ENGAGE) {
            // Fix the £ symbol encoding in XML receipts.
            // We might not need the if statement wrapping this but it's just to be extra safe to not break
            // existing integrations.
            tcpSocket.setEncoding('utf8');
        }

        tcpSocket.onError = (errorString) => {
            this.toastFactory.error(
                'PAYMENT.TERMINAL_ERROR',
                'There was a problem communicating with the Verifone payment terminal.<br/>' + errorString
            );
            this.dismissLoggingInNotification();
        };

        // Add listener for data coming from the TCP socket.
        tcpSocket.onData = (dataString, socket) => {
            let record = this.verifoneResponseRecordFactory.buildRecordFromString(
                dataString,
                socket.name,
                this.state.lastRecordSent
            );

            if (record) {
                this.handleReceivedRecord(record, socket);
            }
        };

        if (tcpSocket instanceof DebouncedTcpSocketConnection) {
            tcpSocket.setSendMessageInterval(500);
        }

        return tcpSocket;
    },

    /**
     * Do something with a record received from the terminal.
     * Then pass it on to any listeners.
     *
     * @param {object} record
     * @param {TcpSocketConnection} socket
     */
    handleReceivedRecord(record, socket) {
        this.log.debug('Record Received (' + socket.name + ')', record);

        this.setLastReceivedRecord(record, socket);
        let terminalError = false;

        if (record.statusName && record.statusName === VerifoneStatusNames.LOGIN_REQUIRED) {
            // "Login required" status received. This is different from attempting to start a transaction
            // while not logged in. This no longer automatically logs in for this status as it caused an
            // infinite loop. But the 'if' remains to prevent the default handler for status messages (below) running
            // for this status.
            // this.$timeout(() => {
            //     this.login();
            // }, 1000);

        } else if (record instanceof VerifoneVoucherRecord) {
            // Print it.
            this.printVoucherRecord(record);
            return;
        } else if (record instanceof VerifoneLoginResponseRecord) {
            if (record.result === 0) {
                // Successful login.
                this.onLogin();
            } else if (record.result === -84) {
                // User already logged in. This is fine!
                this.onLogin();
            }
        } else if (record instanceof VerifoneLogoutResponseRecord) {
            let successfulLogout = false;
            if (record.result === 0) {
                // Successful logout.
                successfulLogout = true;
                this.onLogout();
            } else if (record.result === -84) {
                // User not logged in. This is fine - we wanted to logout anyway.
                successfulLogout = true;
                this.onLogout();
            }

            if (this.logoutRequestCallbacks) {
                if (successfulLogout) {
                    this.logoutRequestCallbacks.resolve(record);
                } else {
                    this.logoutRequestCallbacks.reject(record);
                }
            }
        } else if (record instanceof VerifoneStatusRecord) {
            // For some records we already know what to do, so send a response automatically.
            this.autoRespondToStatusRecord(record);

            // Set the ready status.
            if (record.statusName === VerifoneStatusNames.READY) {
                this.setReady(true);
            } else {
                this.setReady(false);
            }

            // Do some special things with certain statuses.
            switch (record.statusName) {
                case VerifoneStatusNames.DOWNLOADING_FILE:
                case VerifoneStatusNames.PERFORMING_DOWNLOAD:
                    this.maximisePosClient();
                    this.showUpdatingNotification();
                    break;

                case VerifoneStatusNames.DOWNLOAD_COMPLETE:
                    this.dismissUpdatingNotification();
                    break;

                case VerifoneStatusNames.SERVER_CONNECTION_FAILED:
                    // Show modal to go offline or try to reconnect.
                    this.showOfflineConfirmationModal();
                    return;

                case VerifoneStatusNames.CONTINUE_REQUIRED: {
                    let p;

                    if (this.config.improvePrinting) {
                        // Print a small blank page to cut the paper.
                        p = this.printBlankPage()
                            .then(() => timeoutPromise(500));
                    } else {
                        // Needs a long enough timeout to render and print the XML receipt.
                        p = timeoutPromise(2000);
                    }

                    p.then(() => this.sendRecord(
                        record.getActionRecordByName(VerifoneActionNames.CONTINUE_TRANSACTION)
                    ));
                    break;
                }

                case VerifoneStatusNames.MERCHANT_RECEIPT:
                    this.lastMerchantReceiptRecord = record;
                    this.log.debug('Printing record', record);
                    this.printXmlRecord(record).then(() => {});
                    break;

                case VerifoneStatusNames.CUSTOMER_RECEIPT:
                    this.lastCustomerReceiptRecord = record;
                    this.log.debug('Printing record', record);
                    this.printXmlRecord(record).then(() => {});
                    break;

                case VerifoneStatusNames.SIGNATURE_CONFIRMATION_REQUIRED:
                    if (this.config.improvePrinting) {
                        // Print a small blank page to cut the paper.
                        // But don't automatically continue the transaction like for CONTINUE_REQUIRED.
                        this.printBlankPage();
                    }
                    break;
            }
        } else if (record instanceof VerifoneSubmitOfflineTransactionsResponseRecord) {
            if (this.submitOfflineTransactionRequestCallbacks) {
                if (record.result === 0) {
                    this.submitOfflineTransactionRequestCallbacks.resolve(record);
                } else {
                    this.submitOfflineTransactionRequestCallbacks.reject(record);
                }
            }
        }

        if (record.hasOwnProperty('result')) {
            if (record.result < 0) {
                terminalError = true;
                // Generic error handler.
                this.displayErrorRecord(record);
                this.dismissLoggingInNotification();
                this.dismissUpdatingNotification();
            } else if (!this.config.posClient) {
                this.setReady(true);
            }
        }

        let uppercaseMessage = record.message ? record.message.toUpperCase() : '';
        switch (uppercaseMessage) {
            case 'RECEIPT REPRINTED':
                if (this.config.posClient && this.config.improvePrinting) {
                    this.printBlankPage();
                }
                break;
            case 'TRANSACTION CANCELLED':
                if (!this.config.posClient) {
                    this.setReady(true);
                }
                break;
        }

        // If there has been an error such as SERVICE NOT ALLOWED then we need to update the state so that the user
        // is able to cancel and exit the modal or use a different payment method.
        this.state.canCancel = terminalError ? true : this.canCancel();

        // Tell other places that may be interested in this record (the payment controller).
        this.notifyResponseListeners(record, socket);
    },

    /**
     * Set the last record that was received from the terminal on that socket.
     *
     * @param {object} record
     * @param {TcpSocketConnection} socket
     */
    setLastReceivedRecord(record, socket) {
        // Set the last received record on this socket.
        if (socket && socket.name === VerifoneSocketNames.INTEGRATION) {
            this.state.lastIntegrationRecordReceived = record;
        } else if (socket && socket.name === VerifoneSocketNames.PROGRESS) {
            this.state.lastProgressRecordReceived = record;
        }
    },

    /**
     * Display an error notification for the given record.
     *
     * @param {object} record
     */
    displayErrorRecord(record) {
        // Ignore these errors:
        let ignoreErrors = [
            // "User not logged in" when starting a transaction.
            // This is ignored because the message will be displayed in the payment modal.
            -85,
            // "User already logged in" when trying to log in, or "User not logged in" when trying to log out.
            -84,
            // Transaction cancelled - not really an error.
            -99,
        ];

        if (ignoreErrors.indexOf(record.result) === -1) {
            // This is an error we can't handle on the EPOS.
            // Maximise the POS Client app.
            let message = 'There was a error with the Verifone payment terminal:<br/>' + record.message;
            if (this.config.posClient) {
                message += '<br/><small>Please check the terminal application for any further information.</small>';
            }
            this.toastFactory.errorTop('', message);

            this.maximisePosClient();
        }
    },

    /**
     * Set if the terminal is ready to receive a new transaction.
     *
     * @param {boolean} ready
     */
    setReady(ready) {
        this.state.isReady = ready;

        if (this.state.isReady) {
            this.dismissLoggingInNotification();
            this.dismissUpdatingNotification();
        }

        // Show a ready notification once per session.
        if (this.state.isReady && !this.state.initialReady) {
            this.toastFactory.success('', 'Payment terminal is ready.');
            this.state.initialReady = true;
        }
    },

    /**
     * For some status records we want to automatically send an action response.
     *
     * @param {VerifoneStatusRecord} statusRecord
     *
     * @return {boolean} If an automatic response was sent.
     */
    autoRespondToStatusRecord(statusRecord) {
        const autoResponseRecord = this.getAutoResponseRecordForStatusRecord(statusRecord);
        if (autoResponseRecord) {
            this.log.debug(`Auto-responding to ${statusRecord.statusName} with ${autoResponseRecord.actionName}`);
            this.sendRecord(autoResponseRecord);
            return true;
        }

        return false;
    },

    /**
     * @param {VerifoneStatusRecord} statusRecord
     *
     * @return {?VerifoneContinueTransactionRecord}
     */
    getAutoResponseRecordForStatusRecord(statusRecord) {
        switch (statusRecord.statusName) {
            case VerifoneStatusNames.UNEXPECTED_LOGIN:
                // Automatically switch account when logging in as a different user.
                return statusRecord.getActionRecordByName(VerifoneActionNames.REPLACE_ACCOUNT);

            case VerifoneStatusNames.REGISTER_FOR_ACCOUNT_ON_FILE_DECISION:
                // Skip registering for account on file if prompted.
                // The flag to suppress the register for account prompt is set on the transaction request
                // record. But just in case!
                return statusRecord.getActionRecordByName(
                    VerifoneActionNames.ACCOUNT_ON_FILE_REGISTRATION_NOT_REQUIRED
                );

            case VerifoneStatusNames.DOWNLOAD_STILL_BEING_PREPARED:
            case VerifoneStatusNames.RETRY_DOWNLOAD:
            case VerifoneStatusNames.RESUME_DOWNLOAD:
                // If there are errors updating the terminal, cancel the download.
                // This will cause an error message to flash up saying to check the Sential app, which is better
                // than attempting to fix the terminal's problems from the EPOS.
                return statusRecord.getActionRecordByName(VerifoneActionNames.CANCEL_DOWNLOAD);

            case VerifoneStatusNames.UNSAFE_DOWNLOAD:
                return statusRecord.getActionRecordByName(VerifoneActionNames.REJECT_UNSAFE_DOWNLOAD);

            case VerifoneStatusNames.WAITING_FOR_CASHBACK:
                // If cashback is not enabled on the company, auto reply to skip cashback.
                if (!this.userService.company.maxCashback) {
                    return statusRecord.getActionRecordByName(VerifoneActionNames.CASHBACK_NOT_REQUIRED);
                }
                break;
        }

        return null;
    },

    /**
     * Send a record to the terminal.
     *
     * @param {AbstractVerifoneRequestRecord} record
     */
    sendRecord(record) {
        if (record instanceof VerifoneCustomReprintMerchantReceiptRecord) {
            this.reprintLastMerchantReceipt();
            return;
        }

        this.preProcessRecord(record);

        this.state.lastRecordSent = record;

        this.log.debug('Sending Record', record);

        let dataString = record.toString(this.config.integrationType);

        if (this.progressSocket !== null) {
            // Ensure the progress socket is connected first.
            // The integration socket will connect itself before sending data.
            this.progressSocket.connect().then(() => {
                this.integrationSocket.sendData(dataString);
            });
        } else {
            this.integrationSocket.sendData(dataString);
        }

        this.state.canSendRecord = false;
        this.$timeout(() => {
            this.state.canSendRecord = true;
        }, 1000);
    },

    /**
     * Tell the POS Client application to make itself visible.
     */
    maximisePosClient() {
        if (this.config.integrationType === VerifoneIntegrationType.POS_CLIENT) {
            this.sendRecord(new VerifoneWinStateRequestRecord(2));
        }
    },

    /**
     * Perform any necessary transformations on a record before sending.
     *
     * @param {object} record
     */
    preProcessRecord(record) {
        if (record instanceof VerifoneContinueTransactionRecord) {
            switch (record.actionName) {
                case VerifoneActionNames.REPLACE_ACCOUNT:
                    // Replace Account needs to include the manager PIN.
                    record.parameters.MGRPIN = this.config.managerPin;
                    break;
            }
        }
    },

    /**
     * Send a cancel transaction request to the terminal, but only if we know it's appropriate at this time.
     *
     * @return {boolean}
     */
    cancelTransaction() {
        // canCancel() can be true false or null. If false it should not be attempted.
        // If true we know it can be cancelled.
        // If null it may be cancellable. Try it for POS Client. Don't tell the operator to do it for eVo.
        if (this.canCancel() === true) {
            let cancelRecord = this.createActionRecord(VerifoneActionNames.CANCEL_TRANSACTION);
            if (cancelRecord) {
                this.sendRecord(cancelRecord);
            } else {
                // VX820IP doesn't support cancel records. The best we can do is tell the operator to press
                // the cancel button.
                this.toastFactory.warningTop('Please press the Cancel button on the payment terminal.');
            }
            return true;
        }
        this.log.error('Cannot cancel transaction in current state.');
        return false;
    },

    /**
     * @param {string} actionName
     *
     * @return {?VerifoneContinueTransactionRecord}
     */
    createActionRecord(actionName) {
        const action = this.config.actions.findByName(actionName);
        if (action) {
            return new VerifoneContinueTransactionRecord(
                actionName,
                action.id,
                action.message
            );
        }

        return null;
    },

    /**
     * Check if the 'cancel' command can be sent in the current state.
     *
     * @return {boolean|null} If a transaction is in progress the modal should not be closed or the payment
     *                        method changed: false.
     *                        If it is okay to abandon the transaction (no cancel command sent): null.
     *                        Can cancel and the cancel command should be sent: true.
     */
    canCancel() {
        if (this.state.lastProgressRecordReceived && this.state.lastProgressRecordReceived.statusId) {
            // Check if the status specifically says it can be cancelled.
            if (this.state.lastProgressRecordReceived.canPerformAction(VerifoneActionNames.CANCEL_TRANSACTION)) {
                return true;
            }

            // Check if is in a state that can be abandoned.
            // If it is return null (can be left without sending the cancel command).
            // if it is not return false. We know it cannot be cancelled because the cancel action is not available.
            const abandonableStatuses = [
                VerifoneStatusNames.READY,
                VerifoneStatusNames.DOWNLOADING_FILE,
                VerifoneStatusNames.UPDATING_PED,
                VerifoneStatusNames.PERFORMING_DOWNLOAD,
                VerifoneStatusNames.LOGIN_REQUIRED,
                VerifoneStatusNames.TRANSACTION_CANCELLED,
                VerifoneStatusNames.INITIALISING_PED,
                VerifoneStatusNames.PED_UNAVAILABLE,
                VerifoneStatusNames.RETRY_DOWNLOAD,
                VerifoneStatusNames.RESTART_AFTER_SOFTWARE_UPDATE,
                VerifoneStatusNames.DOWNLOAD_COMPLETE,
            ];

            return abandonableStatuses.indexOf(this.state.lastProgressRecordReceived.statusName) !== -1 ? null : false;
        } else if (this.state.lastIntegrationRecordReceived) {
            // The VX820IP terminals do not send status/progress records with a statusId. Determine if it can
            // be cancelled based on the last integration message received.
            let shouldCancelMessages = [
                'Waiting for card',
            ];
            if (shouldCancelMessages.indexOf(this.state.lastIntegrationRecordReceived.message) !== -1) {
                return true;
            }
            let shouldNotCancelMessages = [
                'Authorising',
                'Cardholder print complete',
                'Checking signature',
                'Confirming',
                'Merchant print complete',
            ];
            if (shouldNotCancelMessages.indexOf(this.state.lastIntegrationRecordReceived.message) !== -1) {
                return false;
            }
        }

        return null;
    },

    /**
     * Send a login record to the terminal using the ID/PIN specified, or the ID/PIN from the config
     * if none are given.
     *
     * @param {number} [userId]
     * @param {number} [userPin]
     */
    login(userId, userPin) {
        userId = userId !== undefined ? userId : this.config.userId;
        userPin = userPin !== undefined ? userPin : this.config.userPin;

        if (!userId || !userPin) {
            return;
        }

        let loginRecord = new VerifoneLoginRequestRecord();
        loginRecord.userId = userId;
        loginRecord.userPin = userPin;

        if (!this.config.posClient) {
            // Lock down menu options.
            loginRecord.menuOptions = '2{*}';
        }

        this.sendRecord(loginRecord);

        this.showLoggingInNotification();
    },

    /**
     * Send a logout request record.
     *
     * @return {Promise<VerifoneLogoutResponseRecord>}
     */
    logout() {
        this.log.debug('Logging out');

        const p = new Promise((resolve, reject) => {
            this.logoutRequestCallbacks = {
                resolve: () => resolve(),
                reject: () => reject(),
            };

            this.sendRecord(new VerifoneLogoutRequestRecord());
        });

        p.finally((() => {
            this.logoutRequestCallbacks = null;
        }));

        return p;
    },

    /**
     * Tell the terminal to submit any offline transactions.
     *
     * @return {Promise<VerifoneSubmitOfflineTransactionsResponseRecord>}
     */
    submitOfflineTransactions() {
        this.log.debug('Submitting offline transactions');

        const p = new Promise((resolve, reject) => {
            this.submitOfflineTransactionRequestCallbacks = {
                resolve: () => resolve(),
                reject: () => reject(),
            };

            this.sendRecord(new VerifoneSubmitOfflineTransactionsRequestRecord());
        });

        p.finally((() => {
            this.submitOfflineTransactionRequestCallbacks = null;
        }));

        return p;
    },

    /**
     * Tell the terminal to reprint the last merchant receipt.
     */
    reprintMerchantReceipt() {
        this.sendRecord(new VerifoneReportRequestRecord(102));
    },

    /**
     * Tell the terminal to reprint the last cardholder receipt.
     */
    reprintCardholderReceipt() {
        this.sendRecord(new VerifoneReportRequestRecord(101));
    },

    /**
     * Print a small blank page.
     * (Used to improve printing from POS Client in RAW mode)
     *
     * @return {Promise}
     */
    printBlankPage() {
        let opts = new PrintJobOptions(PrintType.CARD_RECEIPT);
        return this.receiptPrinter.printBlankPage(opts);
    },

    /**
     * @param {VerifoneStatusRecord} record
     * @param {object} additionalData
     *
     * @return {Promise<any>}
     */
    printXmlRecord(record, additionalData = {}) {
        let xml = record.parameters.XML;
        return this.xmlReceiptRenderer.renderXml(xml, additionalData)
            .then((html) => this.receiptPrinter.printVerifoneCardReceipt(html));
    },

    reprintLastMerchantReceipt() {
        if (this.lastMerchantReceiptRecord) {
            return this.printXmlRecord(this.lastMerchantReceiptRecord, { Duplicate: true });
        }

        return Promise.reject(new Error('No merchant receipt record stored.'));
    },

    reprintLastCustomerReceipt() {
        if (this.lastCustomerReceiptRecord) {
            return this.printXmlRecord(this.lastCustomerReceiptRecord, { Duplicate: true });
        }

        return Promise.reject(new Error('No customer receipt record stored.'));
    },

    /**
     * @param {boolean} value
     */
    setExtendedReceipts(value) {
        this.xmlReceiptRenderer.setExtendedReceipts(value);
    },

    /**
     * Register a new listener that will be fired when a record is received from the terminal.
     *
     * @param {Function} listener
     */
    onRecordReceived(listener) {
        this.recordReceivedListeners.push(listener);
    },

    /**
     * Un-register all record received listeners.
     */
    clearRecordReceivedListeners() {
        this.recordReceivedListeners = [];
    },

    /**
     * Show the "Logging in to payment terminal" notification.
     */
    showLoggingInNotification() {
        if (typeof this.dismissLoggingInNotificationFunc !== 'function') {
            let loadingToast = this.toastFactory.spinner('', 'Logging in to payment terminal...');
            this.dismissLoggingInNotificationFunc = () => {
                this.toastFactory.clear(loadingToast);
            };
        }
    },

    /**
     * Hide the "Logging in to payment terminal" notification.
     */
    dismissLoggingInNotification() {
        if (typeof this.dismissLoggingInNotificationFunc === 'function') {
            this.dismissLoggingInNotificationFunc();
            this.dismissLoggingInNotificationFunc = null;
        }
    },

    /**
     * Show the "Payment terminal updating" notification.
     */
    showUpdatingNotification() {
        if (typeof this.dismissUpdatingNotificationFunc !== 'function') {
            let loadingToast = this.toastFactory.spinner('PLEASE_WAIT', 'Payment terminal is updating.');
            this.dismissUpdatingNotificationFunc = () => {
                this.toastFactory.clear(loadingToast);
            };
        }
    },

    /**
     * Hide the "Payment terminal updating" notification.
     */
    dismissUpdatingNotification() {
        if (typeof this.dismissUpdatingNotificationFunc === 'function') {
            this.dismissUpdatingNotificationFunc();
            this.dismissUpdatingNotificationFunc = null;
        }
    },

    /**
     * Notify all the registered response listeners.
     *
     * @param {object} record
     * @param {TcpSocketConnection} socket
     *
     * @private
     */
    notifyResponseListeners(record, socket) {
        for (let r = 0; r < this.recordReceivedListeners.length; r++) {
            this.recordReceivedListeners[r](record, socket);
        }
    },

    /**
     * @private
     */
    onLogin() {
        this.dismissLoggingInNotification();

        this.state.loggedIn = true;

        if (!this.config.posClient) {
            this.setReady(true);
        }
    },

    /**
     * @private
     */
    onLogout() {
        if (this.state.loggedIn) {
            this.toastFactory.success('', 'Successfully logged out of payment terminal.');
        }
        this.state.loggedIn = false;
    },

    /**
     * @param {VerifoneVoucherRecord} record
     * @private
     */
    printVoucherRecord(record) {
        this.receiptPrinter.printVerifoneVoucher(record);
    },

    /**
     * Display a modal to confirm if the payment terminal should switch to offline model.
     *
     * @private
     */
    showOfflineConfirmationModal() {
        this.dismissLoggingInNotification();

        if (this.state.offlineConfirmationPending) {
            return;
        }
        this.state.offlineConfirmationPending = true;

        this.dialogService
            .confirm(
                'PAYMENT_VERIFONE.OFFLINE_MODAL_BODY',
                {
                    cancelLabel: 'PAYMENT_VERIFONE.GO_OFFLINE_BTN',
                    okLabel: 'PAYMENT_VERIFONE.ATTEMPT_RECONNECT_BTN',
                }
            )
            .then((confirmed) => {
                let continueRecord;
                if (confirmed) {
                    continueRecord = this.createActionRecord(VerifoneActionNames.ATTEMPT_TO_RECONNECT);
                } else {
                    continueRecord = this.createActionRecord(VerifoneActionNames.GO_OFFLINE);
                }
                if (continueRecord) {
                    this.sendRecord(continueRecord);
                    this.showLoggingInNotification();
                }
                this.state.offlineConfirmationPending = false;
            });
    },

    /**
     * This is useful if a terminal gets stuck after printing because it hasn't heard a continue
     * transaction record.
     */
    sendContinueTransactionRecord() {
        this.sendRecord(
            this.createActionRecord(
                VerifoneActionNames.CONTINUE_TRANSACTION
            )
        );
    },
};

module.exports = VerifoneDriver;
